<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import CalendarDaySummary from "../../../../../_shared/_modules/calendar-day-summary"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { mdiCalendarBlankMultiple, mdiCalendarSyncOutline, mdiPlus } from "@mdi/js"
  import type { BoardActions, BoardColumn } from "../../../../../../../_logic/calendar-types"
  import {
    SCHEDULING_ACTION_TOOLTIP_CLASS,
    schedulingActionTooltips,
  } from "../../../../../../../../../../_shared/_logic/action-tooltips"

  interface Props {
    column: BoardColumn
    formatDayHeader: (day: Date) => string
    actions: BoardActions
    showCancelReplan?: boolean
    cancelReplanDisabled?: boolean
    isPastDay?: boolean
    readOnly?: boolean
    allowSlotCreation?: boolean
    allowBlockerCreation?: boolean
  }

  let {
    column,
    formatDayHeader,
    actions,
    showCancelReplan = false,
    cancelReplanDisabled = false,
    isPastDay = false,
    readOnly = false,
    allowSlotCreation = true,
    allowBlockerCreation = allowSlotCreation,
  }: Props = $props()

  const employeeNameColor = $derived(
    readOnly
      ? "var(--cal-readonly-header-text, var(--cal-header-text, var(--column-accent, var(--cal-accent, #741e20))))"
      : "var(--cal-header-text, var(--column-accent, var(--cal-accent, #741e20)))",
  )
  const dayLabelColor = $derived(
    readOnly
      ? "var(--cal-readonly-header-muted, var(--cal-header-muted, var(--cal-text-muted, #6d97b0)))"
      : "var(--cal-header-muted, var(--cal-text-muted, #6d97b0))",
  )
  const employeeNameStyle = $derived(`color:${employeeNameColor} !important;opacity:1 !important;`)
  const dayLabelStyle = $derived(`color:${dayLabelColor} !important;opacity:1 !important;`)
</script>

<header class="column-header" class:past-day={isPastDay} class:readonly={readOnly}>
  <div class="column-title">
    <p
      class="employee-name"
      title={column.employee?.label ?? "Unbekannter Nutzer"}
      style={employeeNameStyle}
    >
      {column.employee?.label ?? "Unbekannter Nutzer"}
    </p>
    <p class="day-label" style={dayLabelStyle}>{formatDayHeader(column.day)}</p>
  </div>
  {#if !readOnly && (showCancelReplan || allowBlockerCreation || allowSlotCreation)}
    <div class="header-actions">
      {#if showCancelReplan}
        <button
          type="button"
          class={`cancel-button ${cancelReplanDisabled ? "disabled" : ""}`}
          aria-label="Termine dieses Tages verschieben"
          title="Termine dieses Tages verschieben"
          disabled={cancelReplanDisabled}
          use:tooltip={{
            content: cancelReplanDisabled
              ? schedulingActionTooltips.dayRescheduleUnavailable
              : schedulingActionTooltips.dayReschedule,
            className: SCHEDULING_ACTION_TOOLTIP_CLASS,
          }}
          onclick={() => {
            if (cancelReplanDisabled) return

            actions.startCancelReplanDay(column.day, column.employee?.id ?? null)
          }}
        >
          <Icon path={mdiCalendarSyncOutline} size="0.9rem" />
        </button>
      {/if}
      {#if allowBlockerCreation}
        <button
          type="button"
          class="blocker-button"
          aria-label="Blocker hinzufügen"
          use:tooltip={{
            content: "Blocker für diesen Tag anlegen.",
            className: SCHEDULING_ACTION_TOOLTIP_CLASS,
          }}
          title="Blocker hinzufügen"
          onclick={() => {
            actions.addBlockerSlot({
              day: column.day,
              dayTs: column.day.getTime(),
              employeeId: column.employee?.id,
            })
          }}
        >
          <Icon path={mdiCalendarBlankMultiple} size="0.9rem" />
        </button>
      {/if}
      {#if allowSlotCreation}
        <button
          type="button"
          class="add-button"
          aria-label="Termin hinzufügen"
          use:tooltip={{
            content: schedulingActionTooltips.calendarCreateAppointment,
            className: SCHEDULING_ACTION_TOOLTIP_CLASS,
          }}
          title="Termin hinzufügen"
          onclick={() => {
            actions.addHardSlot({
              day: column.day,
              dayTs: column.day.getTime(),
              employeeId: column.employee?.id,
            })
          }}
        >
          <Icon path={mdiPlus} size="0.9rem" />
        </button>
      {/if}
    </div>
  {/if}
  {#if column.daySummary}
    <div class="header-summary"><CalendarDaySummary summary={column.daySummary} /></div>
  {/if}
</header>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .column-header {
    position: sticky;
    top: var(--calendar-sticky-header-top, 0px);
    z-index: var(--calendar-sticky-header-z-index, 40);
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    row-gap: 0;
    padding: 0.75rem 0.9rem;
    border-bottom: 1px solid var(--cal-border, rgba(0, 0, 0, 0.06));
    background: var(--cal-surface, #eceaea);
    box-shadow: 0 8px 18px rgba(47, 72, 88, 0.08);
    min-width: 0;
  }

  .header-summary {
    flex-basis: 100%;
    min-width: 0;
  }

  .column-header.past-day {
    background: color-mix(in srgb, var(--cal-surface, #eceaea) 65%, transparent);
  }

  .column-header.past-day p.day-label,
  .column-header.past-day p.employee-name {
    color: color-mix(in srgb, var(--cal-text-muted, #6d97b0) 70%, transparent);
  }

  .column-header.readonly p.employee-name {
    color: var(
      --cal-readonly-header-text,
      var(--cal-header-text, var(--column-accent, var(--cal-accent, #741e20)))
    ) !important;
    opacity: 1 !important;
  }

  .column-header.readonly p.day-label {
    color: var(
      --cal-readonly-header-muted,
      var(--cal-header-muted, var(--cal-text-muted, #6d97b0))
    ) !important;
    opacity: 1 !important;
  }

  .column-title {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1 1 auto;
  }

  .employee-name {
    margin: 0;
    font-weight: 800;
    color: var(--cal-header-text, var(--column-accent, var(--cal-accent, #741e20)));
    text-transform: uppercase;
    letter-spacing: 0.04em;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .day-label {
    margin: 0;
    color: var(--cal-header-muted, var(--cal-text-muted, #6d97b0));
    font-size: 0.9rem;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex: 0 0 auto;
  }

  .add-button,
  .blocker-button,
  .cancel-button {
    background: var(--column-accent, var(--cal-accent, #741e20));
    color: var(--neutral-white, #ffffff);
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
      background-color 0.12s ease,
      color 0.12s ease,
      opacity 0.12s ease;

    &:hover:not(:disabled):not(.disabled) {
      opacity: 0.86;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .blocker-button {
    background: color-mix(in srgb, var(--cal-text, #2f4858) 78%, transparent);
  }

  .cancel-button {
    background: color-mix(in srgb, var(--cal-surface, #eceaea) 65%, transparent);
    color: var(--error-200, #b91c1c);
    border: 1px solid color-mix(in srgb, var(--error-200, #b91c1c) 60%, transparent);
  }
</style>
